"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import React, { useCallback } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";
import { addMessage } from "@/components/Messages";
import { GoogleLogo } from "@/components/svgs/Google";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useGoogleOAuth } from "@/lib/auth/useGoogleOAuth";
import { getUserSignUpMetadata } from "@/lib/auth/utils";
import Link from "@/lib/locale/Link";
import { useSignInEmailPassword } from "@/lib/nhost/react";
import type { AuthEmailPasswordFormData } from "../sign-up/components";

type SignInFormFieldsProps = {
  defaultValues?: AuthEmailPasswordFormData;
  isGoogleLoading?: boolean;
  isLoading?: boolean;
  onGoogleSignIn: () => void;
  onSubmit: SubmitHandler<AuthEmailPasswordFormData>;
};

export const SignInFormFields = ({
  defaultValues = { email: "", password: "" },
  isGoogleLoading = false,
  isLoading = false,
  onGoogleSignIn,
  onSubmit,
}: SignInFormFieldsProps) => {
  const t = useTranslations();
  const formSchema = z.object({
    email: z.string().email({
      message: t("auth.emailInputError"),
    }),
    password: z.string().min(9, {
      message: t("auth.passwordInputError"),
    }),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  return (
    <div className="mx-4 w-full max-w-sm rounded-lg border bg-card p-4 md:p-8">
      <h1 className="text-center text-2xl font-semibold">{t("auth.signInHeader")}</h1>
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-lg">{t("auth.emailInputLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      className="text-lg"
                      type="email"
                      placeholder={t("auth.emailInputPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-lg">{t("auth.passwordInputLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      className="text-lg"
                      type="password"
                      placeholder={t("auth.passwordInputPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full text-lg" loading={isLoading}>
              {t("auth.signInSubmitButton")}
            </Button>
          </form>
        </Form>
        <Button
          aria-busy={isGoogleLoading}
          disabled={isGoogleLoading}
          onClick={onGoogleSignIn}
          className="w-full mt-4 space-x-2 text-lg"
          variant="outline"
          type="button"
        >
          <span>{t("auth.signInWithGoogleButton")}</span>
          <GoogleLogo className="w-8" />
        </Button>
      </div>
      <div className="mt-8 space-y-4 text-foreground/80 text-sm text-center">
        {t.rich("auth.noUserRegister", {
          prefix: (chunks) => <span>{chunks}</span>,
          registerLink: (chunks) => (
            <Link href="/app/auth/sign-up" className="underline">
              {chunks}
            </Link>
          ),
        })}
        <div>
          <Link href="/app/auth/forgot-password" className="underline">
            {t("auth.forgottenPasswordLink")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export const SignInForm = ({ toPath }: { toPath: string }) => {
  const t = useTranslations();

  const handleSubmitForm: SubmitHandler<AuthEmailPasswordFormData> = async (formData) => {
    const response = await signInEmailPassword(formData.email, formData.password);

    if (response.isSuccess) return;

    if (response.needsEmailVerification) {
      addMessage({
        text: t("auth.needEmailValidationMessage"),
        type: "ERROR",
      });
    } else {
      console.warn(response);
      addMessage({
        text: response.error?.message || t("auth.signInErrorMessage"),
        type: "ERROR",
      });
    }
  };

  const prepareGoogleAttempt = useCallback(
    () => ({
      destination: toPath,
      metadata: { ...getUserSignUpMetadata(), signUpMethod: "google" },
    }),
    [toPath]
  );
  const handleGoogleFailure = useCallback(() => {
    addMessage({ text: t("auth.googleOAuthStartError"), type: "ERROR" });
  }, [t]);
  const { isStarting, startGoogleOAuth } = useGoogleOAuth({
    entryPoint: "sign-in",
    onFailure: handleGoogleFailure,
    prepareAttempt: prepareGoogleAttempt,
  });

  const { signInEmailPassword, isLoading } = useSignInEmailPassword();

  return (
    <SignInFormFields
      isLoading={isLoading}
      isGoogleLoading={isStarting}
      onGoogleSignIn={startGoogleOAuth}
      onSubmit={handleSubmitForm}
    />
  );
};
